import React, { useState, useRef } from 'react';
import type { ScriptStreamStep } from '../api';

console.log('>>> ScriptCardDeck.tsx STATIC v3 - 20240611-2342');

// STATIC VERSION - NO ANIMATIONS AT ALL
// Only navigation via bottom button

const CARD_BORDER = '1px solid rgba(255,255,255,0.07)';

// Tła SVG dla różnych statusów filtru
const CARD_BG_OK = 'linear-gradient(180deg, rgba(0, 153, 51, 0.084) 4.81%, rgba(0, 255, 85, 0.21) 56.33%, rgba(0, 153, 51, 0.084) 100%)';

// Czerwone tło dla MOBBING_WARNING (z SVG użytkownika)
const CARD_BG_MOBBING = 'linear-gradient(180deg, rgba(255, 0, 0, 0.25) 4.81%, rgba(255, 0, 0, 0.6) 56.33%, rgba(255, 0, 0, 0.25) 100%)';

// Żółte/pomarańczowe tło dla PRIVACY_WARNING i NOT_BUSINESS (z SVG użytkownika)
const CARD_BG_WARNING = 'linear-gradient(180deg, rgba(255, 187, 0, 0.27) 4.81%, rgba(255, 187, 0, 0.8) 52.88%, rgba(255, 187, 0, 0.27) 100%)';

function getCardBg(guardStatus?: string | null): string {
  switch (guardStatus) {
    case 'MOBBING_WARNING':
      return CARD_BG_MOBBING;
    case 'PRIVACY_WARNING':
    case 'NOT_BUSINESS':
      return CARD_BG_WARNING;
    case 'OK':
    default:
      return CARD_BG_OK;
  }
}

interface ScriptCardDeckProps {
  briefing: string | null;
  complianceWarning?: string;
  steps: ScriptStreamStep[];
  streamDone: boolean;
  streamLoading: boolean;
  model?: string;
  fontSize: number;
  onConfirmLast: () => void;
  onNextRef?: React.MutableRefObject<(() => void) | null>;
  guardStatus?: 'OK' | 'MOBBING_WARNING' | 'PRIVACY_WARNING' | 'NOT_BUSINESS' | null;
}

export const ScriptCardDeck: React.FC<ScriptCardDeckProps> = ({
  briefing, complianceWarning, steps, streamDone, streamLoading, fontSize, onConfirmLast, onNextRef,
  guardStatus,
}) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState<'briefing' | 'cards'>('briefing');
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 50;
  const AXIS_LOCK_THRESHOLD = 10;

  // Przycisk: briefing → karty → E3 (woła onConfirmLast na ostatniej karcie)
  // FIX: Nie pozwól na przejście do E3 gdy steps jest puste (jeszcze się ładują)
  const handleNextBtn = () => {
    if (phase === 'briefing') {
      setPhase('cards');
    } else {
      const next = currentIdx + 1;
      if (next >= steps.length) {
        // FIX: Blokuj jeśli brak kroków - nie przeskakuj do E3
        if (steps.length === 0) return;
        onConfirmLast();
      } else {
        setCurrentIdx(next);
      }
    }
  };

  // Swipe right: tylko nawigacja w obrębie kart, nie wyjście do E3
  // FIX: Blokuj swipe gdy brak kroków (jeszcze się ładują)
  const handleNextSwipe = () => {
    if (steps.length === 0) return; // FIX: nie pozwól na nawigację bez kroków
    if (phase === 'briefing') {
      setPhase('cards');
    } else {
      const next = currentIdx + 1;
      if (next < steps.length) {
        setCurrentIdx(next);
      }
      // Na ostatniej karcie swipe right nic nie robi
    }
  };

  const handlePrev = () => {
    if (phase === 'cards') {
      if (currentIdx > 0) {
        setCurrentIdx(currentIdx - 1);
      } else {
        setPhase('briefing');
      }
    }
    // W briefing nie cofamy na E1
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Ignoruj jeśli ruch jest głównie pionowy (scroll treści)
    if (Math.abs(dy) > Math.abs(dx) + AXIS_LOCK_THRESHOLD) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    if (dx < 0) {
      handlePrev();       // swipe left = krok wstecz
    } else {
      handleNextSwipe();  // swipe right = krok wprzód (bez E3)
    }
  };

  React.useEffect(() => {
    if (onNextRef) {
      onNextRef.current = handleNextBtn;
    }
    return () => { if (onNextRef) onNextRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onNextRef, phase, currentIdx, steps.length]);

  const cardStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0, right: 0, top: 0, bottom: 0,
    background: getCardBg(guardStatus),
    border: CARD_BORDER,
    borderRadius: '33px',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 22px 20px',
    overflow: 'hidden',
  };

  // BRIEFING PHASE
  if (phase === 'briefing') {
    return (
      <div 
        style={{ position: 'relative', width: '100%', height: '100%' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div style={cardStyle}>
          <div style={{
            color: 'rgba(0,255,85,0.6)',
            fontSize: '10px', fontWeight: 400,
            fontFamily: '"Inter", sans-serif',
            letterSpacing: '3px', textTransform: 'uppercase',
            marginBottom: '20px',
          }}>
            Odprawa taktyczna
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {complianceWarning && (
              <div style={{
                background: 'rgba(255, 180, 0, 0.08)',
                border: '1px solid rgba(255, 180, 0, 0.25)',
                borderRadius: '12px',
                padding: '12px 14px',
                marginBottom: '16px',
              }}>
                <div style={{ color: 'rgba(255, 200, 0, 0.85)', fontSize: '10px', fontWeight: 500 }}>
                  ⚠️ Uwaga taktyczna
                </div>
                <p style={{ color: 'rgba(255,220,100,0.80)', fontSize: `${Math.max(fontSize - 1, 12)}px`, margin: 0 }}>
                  {complianceWarning}
                </p>
              </div>
            )}
            {briefing ? (
              <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: `${Math.max(fontSize + 3, 18)}px`, margin: 0 }}>
                {briefing}
              </p>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: '40px' }}>
                {streamLoading ? 'Ładuję odprawę...' : 'Brak odprawy'}
              </div>
            )}
          </div>

          {briefing && (
            <button
              onClick={handleNextBtn}
              style={{
                marginTop: '20px', padding: '14px 0', width: '100%',
                borderRadius: '16px',
                border: '1px solid rgba(0,255,85,0.3)',
                background: 'radial-gradient(ellipse at center, rgba(0,255,85,0.18) 0%, rgba(0,153,51,0.08) 100%)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '15px', fontFamily: '"Inter", sans-serif',
                letterSpacing: '2px',
                cursor: 'pointer',
              }}
            >
              Zaczynamy →
            </button>
          )}
        </div>
      </div>
    );
  }

  // CARDS PHASE - NO animation, simple replacement
  const currentStep = steps[currentIdx];
  
  if (!currentStep) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div style={cardStyle}>
          <p style={{ color: 'white', textAlign: 'center' }}>
            {streamLoading ? 'Ładuję kroki...' : 'Brak kroków'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={cardStyle}>
        <div style={{
          color: 'rgba(255,255,255,0.28)',
          fontSize: '10px', fontWeight: 400,
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '3px', textTransform: 'uppercase',
          marginBottom: '14px',
        }}>
          Krok {currentStep.step}{steps.length > 0 ? ` / ${streamDone ? steps.length : '…'}` : ''}
        </div>

        <div style={{
          color: 'rgba(0,255,85,0.85)',
          fontSize: `${fontSize - 1}px`, fontWeight: 600,
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '0.5px',
          marginBottom: '16px',
        }}>
          {currentStep.label}
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          <p style={{
            color: 'rgba(255,255,255,0.92)',
            fontSize: `${Math.max(fontSize + 4, 20)}px`,
            fontWeight: 300,
            fontFamily: '"Inter", sans-serif',
            lineHeight: 1.6,
            margin: 0,
          }}>
            {currentStep.phrase}
          </p>
        </div>

        <div style={{
          marginTop: '12px',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '11px',
          textAlign: 'center',
        }}>
          {currentIdx + 1} z {streamDone ? steps.length : '?'} | Naciśnij przycisk poniżej →
        </div>
      </div>
    </div>
  );
};